import * as React from 'react';
import { observer } from "mobx-react";
import autobind from "autobind-decorator";
import { observable, computed, toJS } from "mobx";
import { Button, Modal, Card, Col, Row } from 'antd';
import { resolve } from 'inversify-react';
import * as uuid from 'uuid';
import * as moment from 'moment';
import { connect } from "react-redux";

import { FormatService } from "src/core/services/format.service";
import { TextView } from "src/core/ui/text.view";
import { DropdownView } from 'src/core/ui/dropdown.view';
import { TabLanguageInlineView } from "src/core/ui/language/tab-language-inline.view";
import { CommandBar } from "src/core/ui/command-bar";
import { TooltipView } from "src/core/ui/tooltip.view";
import { BooleanView } from 'src/core/ui/boolean.view';
import { ObjectUtils } from 'src/core/utils/object';
import { BillingTypeView } from './billing-type.view';
import { ZuluIcon } from 'src/app/shared/icon';

interface PropsFromState {
	dates: ContractRangeDateItem[];
	services: ContractServiceTypeItem[];
	markets: ContractMarketItem[];
	product: ProductItem;
}

interface Props extends ComponentViewProps<DataSet<PriceElement>> {
	mode: ControlMode;
	contract: ContractItem;
	type: 'cost' | 'price';
	currency: CurrencySummary;
	currencies: CurrencySummary[];
	selectedMarket?: number;
}

interface DateRange {
	bookingDateFrom: Date;
	bookingDateTo: Date;
	serviceDateFrom: Date;
	serviceDateTo: Date;
	bookingDateFromText: string;
	bookingDateToText: string;
	serviceDateFromText: string;
	serviceDateToText: string;
}

class PriceViewModel {
	@observable
	public prices: PriceElement[] = [];

	@observable
	public isEditing = false;

	@observable
	array: any[];

	constructor(public item: PriceElement, private self: ContractPriceView, startEditing?: boolean) {
		if (item.displayName) {
			this.array = [{ title: "Title", dynamicProperties: item.displayName, required: true }];
		}
		if (startEditing) {
			this.edit();
		}
		this.add(item);
	}

	add(item: PriceElement) {
		const valueProp = this.self.getColumnKey(item);
		observable(this, valueProp);
		this[valueProp] = item.value;
		this.prices.push(item);
	}

	edit() {
		const possibleEdits = this.self.prices ? this.self.prices.filter(e => e.service.productServiceTypeId == this.item.service) : [];
		for (const groupBy of possibleEdits) {
			for (const p of groupBy.prices) {
				if (p.isEditing && p != this) {
					p.isEditing = false;
				}
			}
		}
		this.isEditing = !this.isEditing;
	}

	delete() {
		this.self.props.model.delete(this.prices);
		this.self.prices = this.self.groupByData(this.self.props.model.items.slice());
		this.self.fillServiceOpt(this.self.props.services, this.self.props.model.items);
	}

	addDate(date: DateRange) {
		this.self.props.model.create(() => this.self.createPrice(date, this.item.uuid, {
			productServiceTypeId: this.item.service,
			productServiceTypeDisplayName: this.item.serviceName
		} as any, this.item.type, this.item.currency))
			.then(price => this.add(price));
	}
}

interface GroupedPrices {
	prices: PriceViewModel[];
	service: ContractServiceTypeItem;
}

interface DefaultPaxConfig {
	id: number;
	name: string;
	productType: string[];
	billingType?: BillingType[];
	matchConfig: PaxConfig;
	otherPaxConfig: PaxConfig[];
}

@observer
class ContractPriceView extends React.Component<Props & PropsFromState, any> {
	@resolve
	protected formatService: FormatService;

	@observable
	service: ContractServiceTypeItem;

	@observable
	prices: GroupedPrices[];

	@observable
	showDialog = false;

	cloneService: ContractServiceTypeItem;

	//@observable
	//showPriceConfigDialog: PriceViewModel = null;

	observable
	servicesOpt: any[] = [];

	@computed
	get serviceOptions() {
		//if (this.service != null)
		//    this.servicesOpt = this.servicesOpt.filter(x => x.value != this.service.productServiceTypeId.toString());
		return this.servicesOpt;
	}

	@computed
	get hardCodedModificationsConfigs() {
		let modificationsConfig: DefaultPaxConfig[] = [
			{
				id: 1,
				name: 'Discount for infants',
				productType: ['Accomodation', 'Room'],
				matchConfig: {
					minAge: 0,
					maxAge: 2,
					minPax: 1,
					maxPax: 2,
					minDuration: 0,
					maxDuration: 255
				},
				otherPaxConfig: [
					{
						minAge: 18,
						maxAge: 99,
						minPax: 1,
						maxPax: 2,
						minDuration: 0,
						maxDuration: 255
					}
				]
			},
			{
				id: 2,
				name: 'Discount for children',
				productType: ['Accomodation', 'Room'],
				matchConfig: {
					minAge: 3,
					maxAge: 12,
					minPax: 1,
					maxPax: 2,
					minDuration: 0,
					maxDuration: 255
				},
				otherPaxConfig: [
					{
						minAge: 18,
						maxAge: 99,
						minPax: 1,
						maxPax: 2,
						minDuration: 0,
						maxDuration: 255
					}
				]
			},
			{
				id: 3,
				name: 'Discount for second children',
				productType: ['Accomodation', 'Room'],
				matchConfig: {
					minAge: 3,
					maxAge: 12,
					minPax: 1,
					maxPax: 2,
					minDuration: 0,
					maxDuration: 255
				},
				otherPaxConfig: [
					{
						minAge: 3,
						maxAge: 12,
						minPax: 1,
						maxPax: 1,
						minDuration: 0,
						maxDuration: 255
					},
					{
						minAge: 18,
						maxAge: 99,
						minPax: 1,
						maxPax: 2,
						minDuration: 0,
						maxDuration: 255
					}
				]
			},
			{
				id: 4,
				name: 'Discount for two children, separated room',
				productType: ['Accomodation', 'Room'],
				matchConfig: {
					minAge: 3,
					maxAge: 12,
					minPax: 2,
					maxPax: 2,
					minDuration: 0,
					maxDuration: 255
				},
				otherPaxConfig: [
					{
						minAge: 0,
						maxAge: 99,
						minPax: 0,
						maxPax: 0,
						minDuration: 0,
						maxDuration: 255
					}
				]
			},
			{
				id: 5,
				name: 'Supplement for single room',
				productType: ['Accomodation', 'Room'],
				billingType: ['PerNightPerPerson'],
				matchConfig: {
					minAge: 13,
					maxAge: 99,
					minPax: 1,
					maxPax: 1,
					minDuration: 0,
					maxDuration: 255
				},
				otherPaxConfig: [
					{
						minAge: 0,
						maxAge: 99,
						minPax: 0,
						maxPax: 0,
						minDuration: 0,
						maxDuration: 255
					}
				]
			},
			{
				id: 5,
				name: 'Discount for triple room',
				productType: ['Accomodation', 'Room'],
				billingType: ['PerNightPerPerson'],
				matchConfig: {
					minAge: 13,
					maxAge: 99,
					minPax: 3,
					maxPax: 3,
					minDuration: 0,
					maxDuration: 255
				},
				otherPaxConfig: [
					{
						minAge: 0,
						maxAge: 99,
						minPax: 2,
						maxPax: 2,
						minDuration: 0,
						maxDuration: 255
					}
				]
			}
		];
		const contract = this.props.contract;
		const structure: StructureItem = contract.dynamicProperties[this.props.type].structure;
		modificationsConfig = this.props.product
			? modificationsConfig.filter(e => e.productType.some(t => t == this.props.product.productTypeText) && (e.billingType == null || e.billingType.some(t => t == structure.type)))
			: [];
		return modificationsConfig;
	}


	public fillServiceOpt(services: any[], items: any[]) {
		this.servicesOpt = services ? services
			.filter(e => e.productServiceTypeId != null && !items.some(y => y.service == e.productServiceTypeId))
			.map(service => ({ value: service.productServiceTypeId.toString(), label: service.productServiceTypeDisplayName })) : [];
	}

	constructor(props) {
		super(props);
		this.fillServiceOpt(this.props.services, this.props.model.items);
		this.state = {
			showPriceConfigDialog: null,
			showPriceItem: null,
			showPriceConfigDialogObservable: null,
			defaultPaxConfig: null
		};
	}

	componentDidMount() {
		const model = this.props.model;
		if (model) {
			const market = this.props.selectedMarket == 0 ? null : this.props.selectedMarket;
			this.prices = this.groupByData(model.items.filter(e => e.market == market).slice());

			for (const group of this.prices) {
				const groupPrices = group.prices;
				for (const d of groupPrices) {
					for (const date of this.props.dates) {
						const prop = this.getColumnKey(date);
						if (d[prop] == undefined) {
							d.addDate(date);
						}
					}
				}
			}
		}
	}

	componentWillReceiveProps(nextProps: Readonly<Props & PropsFromState>, nextContext: any) {
		const model = nextProps.model;
		if (model) {
			this.fillServiceOpt(nextProps.services, model.items);

			const market = nextProps.selectedMarket == 0 ? null : nextProps.selectedMarket;
			this.prices = this.groupByData(model.items.filter(e => e.market == market).slice());

			for (const group of this.prices) {
				const groupPrices = group.prices;
				for (const d of groupPrices) {
					for (const date of nextProps.dates) {
						const prop = this.getColumnKey(date);
						if (d[prop] == undefined) {
							d.addDate(date);
						}
					}
				}
			}
		}
	}

	@autobind
	createPrice(date: DateRange, id: string, service?: ContractServiceTypeItem, type?: 'Price' | 'Modification', currency?: number, oldPrice?: PriceViewModel): PriceElement {
		if (this.props.selectedMarket > 0) {
			const market = this.props.markets.find(e => e.marketId == this.props.selectedMarket);
			currency = this.props.currencies.find(e => market.marketCurrencyId == e.id).id;
		}
		const price: PriceElement = {
			uuid: id,
			type: oldPrice ? oldPrice.item.type : type || 'Price',
			service: (service || this.service).productServiceTypeId,
			market: oldPrice ? oldPrice.item.market : this.props.selectedMarket == 0 ? null : this.props.selectedMarket,
			currency: currency || this.props.currency.id,
			bookingDateFrom: date.bookingDateFrom,
			bookingDateTo: date.bookingDateTo,
			serviceDateFrom: date.serviceDateFrom,
			serviceDateTo: date.serviceDateTo,
			minPax: oldPrice ? oldPrice.item.minPax : 1,
			maxPax: oldPrice ? oldPrice.item.maxPax : 1,
			minAge: oldPrice ? oldPrice.item.minAge : 0,
			maxAge: oldPrice ? oldPrice.item.maxAge : 99,
			minDuration: oldPrice ? oldPrice.item.minDuration : 1,
			maxDuration: oldPrice ? oldPrice.item.maxDuration : 255,
			value: oldPrice ? oldPrice[this.getColumnKey(date)] : 0,

			sign: oldPrice ? oldPrice.item.sign : 'Up',
			required: oldPrice ? oldPrice.item.required : true,
			mode: oldPrice ? oldPrice.item.mode : 'Amount',

			serviceName: (service || this.service).productServiceTypeDisplayName,
			bookingDateFromText: date.bookingDateFromText,
			bookingDateToText: date.bookingDateToText,
			serviceDateFromText: date.serviceDateFromText,
			serviceDateToText: date.serviceDateToText
		};
		if (price.type == 'Modification') {
			price.required = oldPrice ? oldPrice.item.required : false;
			price.mode = oldPrice ? oldPrice.item.mode : 'Percent';
			price.displayName = oldPrice ? ObjectUtils.deepCopy(oldPrice.item.displayName) : {
				en: ''
			};
			price.otherPaxsConfig = oldPrice ? oldPrice.item.otherPaxsConfig.map(e => ObjectUtils.deepCopy(e, attr => attr == '__isProxy' || attr == '__edit')) : [];
			price.order = this.prices
				.find(e => e.service.productServiceTypeId == service.productServiceTypeId)
				.prices.filter(e => e.item.type == 'Modification').length + 1;
			price.replaceBasePrice = oldPrice ? oldPrice.item.replaceBasePrice : false;
			price.stopOtherSupplements = oldPrice ? oldPrice.item.stopOtherSupplements : false;
			price.billingType = oldPrice ? oldPrice.item.billingType : this.props.contract.dynamicProperties[this.props.type].structure.type;
		}
		this.servicesOpt = this.servicesOpt.filter(x => x.value != (service || this.service).productServiceTypeId.toString());
		return observable(price);
	}

	private clonePrice = (group: GroupedPrices, price: PriceViewModel) => async () => {
		this.createPrices(group.service, price.item.type, price);
	}

	@autobind
	protected createPrices(service?: ContractServiceTypeItem, type?: 'Price' | 'Modification', oldPrice?: PriceViewModel) {
		const id = uuid.v4();
		Promise.all(this.props.dates.map(date => this.props.model.create(() => this.createPrice(date, id, service, type, undefined, oldPrice))))
			.then(prices => {
				const servicePrices = this.prices.find(p => p.service.productServiceTypeId == (service || this.service).productServiceTypeId);
				if (servicePrices == null) {
					const rows = this.groupByData(this.props.model.items.slice(), true);
					this.prices = rows;
				} else {
					const rows = this.groupByData(prices.slice(), true);
					servicePrices.prices = servicePrices.prices.slice().concat(rows[0].prices[0]);
				}
				this.showDialog = false;
				this.service = null;
			});
	}

	@autobind
	protected newPrice() {
		this.showDialog = true;
	}

	@autobind
	private updatePriceElement(vm: PriceViewModel, field: keyof PriceElement | string, value: any) {
		for (const item of vm.prices) {
			if (Object.getOwnPropertyNames(item).every(p => p != field)) {
				const dates = this.getDates(field);
				if (dates[0].isValid()) {
					if (dates[0].isSame(item.bookingDateFrom, 'day') && dates[1].isSame(item.bookingDateTo, 'day')
						&& dates[2].isSame(item.serviceDateFrom, 'day') && dates[3].isSame(item.serviceDateTo, 'day')) {
						item.value = value;
						vm[field] = value;
						break;
					}
				} else {
					item[field] = value;
				}
			} else {
				item[field] = value;
			}
		}
	}

	private updateOrder(item: PriceViewModel, newOrder: number) {
		const oldOrder = item.item.order;
		const service = item.item.service;
		let startIndex = newOrder < oldOrder ? newOrder : oldOrder + 1;
		while (startIndex <= Math.max(newOrder, oldOrder)) {
			const group = this.prices.find(e => e.service.productServiceTypeId == service);
			if (group == null) continue;
			const modifications = group.prices.filter(e => e.item.type == 'Modification');

			if (modifications.length < newOrder) {
				newOrder = modifications.length;
				startIndex = newOrder < oldOrder ? newOrder : oldOrder + 1;
			}
			if (newOrder < 1) {
				newOrder = 1;
				startIndex = newOrder < oldOrder ? newOrder : oldOrder + 1;
			}

			const prices = group.prices.filter(e => e.item.type == 'Modification' && startIndex == e.item.order);
			if (prices.length == 0) break;

			const order = newOrder < oldOrder ? startIndex++ + 1 : startIndex++ - 1;
			prices[0].item.order = order;
			this.updatePriceElement(prices[0], 'order', order);
			if (startIndex == oldOrder) {
				break;
			}
		}

		this.updatePriceElement(item, 'order', newOrder);
	}

	@autobind
	private async acceptDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (this.cloneService && this.service) {
			const originalPrices: PriceElement[] = this.props.model.items
				.filter(e => e.service == this.cloneService.productServiceTypeId)
				.map(e => ObjectUtils.deepCopy(e, attr => attr == '__isProxy' || attr == '__edit'));
			for (const item of originalPrices) {
				item.service = this.service.productServiceTypeId;
				item.serviceName = this.service.productServiceTypeDisplayName;
				await this.props.model.create(() => item);
			}
			this.servicesOpt = this.servicesOpt.filter(x => x.value != this.service.productServiceTypeId.toString());
			this.cloneService = null;
			const rows = this.groupByData(this.props.model.items.slice(), false);
			this.prices = rows;
			this.service = null;
			this.showDialog = false;
		} else if (this.service) {
			this.createPrices();
		}
	}

	@autobind
	private closeDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		ev.preventDefault();
		this.showDialog = false;
		this.cloneService = null;
		this.service = null;
	}

	render() {
		if (!this.props.dates || !this.props.services) {
			return null;
		}

		let headers = [
			this.props.dates.map(e => <th key={e.id}>
				Booking date <br />
				{e.bookingDateFromText} - {e.bookingDateToText}
			</th>),
			this.props.dates.map(e => <th key={e.id}>
				Service date <br />
				{e.serviceDateFromText} - {e.serviceDateToText}
			</th>)
		];

		let prices = this.prices && this.prices.length ? this.prices : [];
		let data = [];

		let currencyFormat = this.props.currency ? this.props.currency.format : '${0}';
		let currencySymbol = this.props.currency ? this.props.currency.symbol : '$';
		const cost: CostItem = this.props.contract.dynamicProperties['cost'];
		if (this.props.type == 'price' && this.props.selectedMarket > 0 && this.props.markets && this.props.markets.length > 0) {
			const market = this.props.markets.find(e => e.marketId == this.props.selectedMarket);
			const currency = this.props.currencies.find(e => e.id == market.marketCurrencyId);
			currencyFormat = currency.format;
			currencySymbol = currency.symbol;
		}

		if (prices.length)
			data = prices.map((group, index: number) => {
				const groupPrices = group.prices.filter(e => e.item.type == 'Price');
				const groupModifications = group.prices.filter(e => e.item.type != 'Price');
				groupModifications.sort((x, y) => x.item.order - y.item.order);
				return <Card key={index} hoverable={false} className='mb-3' title={
					<CommandBar mode={this.props.mode} items={
						[
							{
								key: group.service.productServiceTypeDisplayName,
								name: group.service.productServiceTypeDisplayName,
								icon: 'close',
								disabled: this.props.mode == 'display',
								onClick: () => {
									for (const price of group.prices) {
										price.delete();
									}
								},
								type: 'danger'
							},
							{
								key: 'add-service-price',
								name: 'Add price',
								icon: "plus",
								disabled: this.props.mode == 'display',
								onClick: () => this.createPrices(group.service)
							},
							{
								key: 'add-service-price-modification',
								name: 'Add price modification',
								icon: "plus",
								disabled: this.props.mode == 'display',
								onClick: () => this.createPrices(group.service, 'Modification')
							},
							{
								key: 'clone-service',
								name: 'Clone price',
								icon: "copy",
								disabled: this.props.mode == 'display',
								onClick: () => {

									this.cloneService = group.service;
									this.newPrice();

								}
							}
						]
					}
					/>
				}>
					<table className="b-table">
						<thead>
							<tr>
								<th rowSpan={2} style={{ width: 64 }}></th>
								<th colSpan={2}>Pax</th>
								<th colSpan={2}>Age</th>
								<th colSpan={2}>Duration</th>
								{
									headers[0]
								}
							</tr>
							<tr>
								<th>Min</th>
								<th>Max</th>
								<th>Min</th>
								<th>Max</th>
								<th>Min</th>
								<th>Max</th>
								{
									headers[1]
								}
							</tr>
						</thead>
						<tbody>
							{
								groupPrices.map((d, i) => <tr key={i} onDoubleClick={d.isEditing || this.props.mode == 'display' ? null : d.edit.bind(d)}>
									<td className='cell'>
										<Button.Group>
											<Button icon={d.isEditing ? 'close' : 'edit'} onClick={d.edit.bind(d)} disabled={this.props.mode == 'display'} />
											<Button icon={'delete'} onClick={d.delete.bind(d)} disabled={this.props.mode == 'display'} />
											<Button icon={'copy'} onClick={this.clonePrice(group, d)} disabled={this.props.mode == 'display'} />
										</Button.Group>
									</td>
									<td style={{ width: 50 }} className={d.isEditing ? 'cell' : ''}>
										{
											!d.isEditing ? d.item.minPax : this.getEditor(d, 'minPax')
										}
									</td>
									<td style={{ width: 50 }} className={d.isEditing ? 'cell' : ''}>
										{
											!d.isEditing ? d.item.maxPax : this.getEditor(d, 'maxPax')
										}
									</td>
									<td style={{ width: 50 }} className={d.isEditing ? 'cell' : ''}>
										{
											!d.isEditing ? d.item.minAge : this.getEditor(d, 'minAge')
										}
									</td>
									<td style={{ width: 50 }} className={d.isEditing ? 'cell' : ''}>
										{
											!d.isEditing ? d.item.maxAge : this.getEditor(d, 'maxAge')
										}
									</td>
									<td style={{ width: 50 }} className={d.isEditing ? 'cell' : ''}>
										{
											!d.isEditing ? d.item.minDuration : this.getEditor(d, 'minDuration')
										}
									</td>
									<td style={{ width: 50 }} className={d.isEditing ? 'cell' : ''}>
										{
											!d.isEditing ? d.item.maxDuration : this.getEditor(d, 'maxDuration')
										}
									</td>
									{
										this.props.dates.map((date, ind) => {
											const prop = this.getColumnKey(date);
											return <td style={{ width: 50 }} className={d.isEditing ? 'cell' : ''} key={ind}>
												{
													!d.isEditing
														? this.formatService.toString(cost.structure.mode == 'Percent' ? 'percent-full' : 'currency', d[prop], currencyFormat)
														: this.getEditor(d, prop, cost.structure.mode == 'Percent' ? '%' : currencySymbol)
												}
											</td>
										})
									}
								</tr>)
							}
							{
								groupModifications.length > 0 &&
								<tr>
									<th colSpan={99}>
										Prices' modifications
									</th>
								</tr>
							}
							{
								groupModifications.length > 0 &&
								groupModifications.map((d, i) => <tr key={i} onDoubleClick={d.isEditing || this.props.mode == 'display' ? null : d.edit.bind(d)}>
									<td className='cell'>
										<Button.Group className='mb-1'>
											<Button
												icon={d.isEditing ? 'close' : 'edit'}
												onClick={d.edit.bind(d)}
												disabled={this.props.mode == 'display'}
											/>
											<Button
												icon={'delete'}
												onClick={d.delete.bind(d)}
												disabled={this.props.mode == 'display'}
											/>
											<Button
												icon={'copy'}
												onClick={this.clonePrice(group, d)}
												disabled={this.props.mode == 'display'}
											/>
											<Button
												icon='up'
												onClick={() => this.updateOrder(d, d.item.order - 1)}
												disabled={this.props.mode == 'display' || d.item.order == 1}
											/>
											<Button
												icon='down'
												onClick={() => this.updateOrder(d, d.item.order + 1)}
												disabled={this.props.mode == 'display' || groupModifications.length == d.item.order}
											/>
										</Button.Group>
									</td>
									<td className='cell' style={{ textAlign: 'left' }} colSpan={6}>
										<Row gutter={16} className='mb-1'>
											<Col xs={12}>
												<Button.Group>
													<TooltipView title={`Current: ${d.item.mode}. Switch between currency amount or percent value`}>
														<Button
															icon={d.item.mode == 'Amount' ? 'dollar' : undefined}
															disabled={this.props.mode == 'display'}
															onClick={() => {
																const mode = d.item.mode == 'Amount' ? 'Percent' : 'Amount';
																this.updatePriceElement(d, 'mode', mode);
															}}
														>
															{d.item.mode == 'Amount' ? null : "%"}
														</Button>
													</TooltipView>
													<TooltipView title={`Current: ${d.item.sign == 'Up' ? 'Positive' : 'Negative'}. Switch between positive or negative amount or percent`}>
														<Button
															icon={d.item.sign == 'Up' ? 'plus-circle-o' : 'minus-circle-o'}
															disabled={this.props.mode == 'display'}
															onClick={() => {
																const sign = d.item.sign == 'Up' ? 'Down' : 'Up';
																this.updatePriceElement(d, 'sign', sign);
															}}>
														</Button>
													</TooltipView>
													<TooltipView title={`Current: ${d.item.required ? 'Required' : 'Optional'}. Set this modification as required or optional`}>
														<Button
															icon={d.item.required ? 'lock' : 'unlock'}
															disabled={this.props.mode == 'display'}
															onClick={() => {
																const required = !d.item.required;
																this.updatePriceElement(d, 'required', required);
															}}>
														</Button>
													</TooltipView>
													{
														d.item.mode == 'Amount' &&
														<TooltipView title={`Current: ${d.item.replaceBasePrice ? 'Replace' : 'Not replace'}. Set this modification to replace base price`}>
															<Button
																icon={d.item.replaceBasePrice ? 'login' : 'logout'}
																disabled={this.props.mode == 'display'}
																onClick={() => {
																	const replaceBasePrice = !d.item.replaceBasePrice;
																	this.updatePriceElement(d, 'replaceBasePrice', replaceBasePrice);
																}}>
															</Button>
														</TooltipView>
													}
													<TooltipView title={`Current: ${d.item.stopOtherSupplements ? 'Stop others' : 'Process others'}. Price modifications are procesed by order, if this is check, it will be the last modification applied.`}>
														<Button
															icon={d.item.stopOtherSupplements ? 'stop-circle-o' : 'play-circle-o'}
															disabled={this.props.mode == 'display'}
															onClick={() => {
																const stopOtherSupplements = !d.item.stopOtherSupplements;
																this.updatePriceElement(d, 'stopOtherSupplements', stopOtherSupplements);
															}}>
														</Button>
													</TooltipView>
													<TooltipView title={this.getConditions(d.item)}>
														<Button
															icon='setting'
															disabled={this.props.mode == 'display'}
															onClick={() => {
																const item = ObjectUtils.deepCopy(d.item, attr => attr == '__isProxy' || attr == '__edit');
																this.setState({
																	showPriceItem: item,
																	showPriceConfigDialog: toJS(d),
																	showPriceConfigDialogObservable: d
																});
															}}>
														</Button>
													</TooltipView>
												</Button.Group>
											</Col>
											<Col xs={12}>
												<BillingTypeView
													label={null}
													mode={this.props.mode}
													model={d.item.billingType}
													required
													onChanged={(item) => { this.updatePriceElement(d, 'billingType', item.value); }}
												/>
											</Col>
										</Row>
										{
											!d.isEditing ? d.item.displayName['en'] :
												<TabLanguageInlineView
													dynamicProperties={d.array}
													mode={this.props.mode}
													setBusy={this.props.setBusy}
													model={this.props.contract}
													onChange={newVal => {
														for (const i of d.prices) {
															for (const lang of Object.getOwnPropertyNames(newVal)) {
																if (typeof newVal[lang] == 'string') {
																	i.displayName[lang] = newVal[lang];
																}
															}
														}
													}}
												/>
										}
									</td>
									{
										this.props.dates.map((date) => {
											const prop = this.getColumnKey(date);
											const priceModification = this.getPriceModification(d, prop, groupPrices);
											return (
												<TooltipView title={priceModification} key={date.id}>
													<td
														style={{ width: 50 }}
														className={d.isEditing ? 'cell' : ''}>
														{
															!d.isEditing
																? this.formatService.toString(d.item.mode == 'Amount' ? 'currency' : 'percent-full', d[prop], currencyFormat)
																: this.getEditor(d, prop, d.item.mode == 'Amount' ? currencySymbol : '%')
														}
													</td>
												</TooltipView>
											);
										})
									}
								</tr>)
							}
						</tbody>
					</table>
				</Card>
			});

		return (
			<div>
				<Modal
					visible={this.showDialog}
					onCancel={this.closeDialog}
					title="Select Service"
					maskClosable={false}
					footer={[
						<Button key='confirm' type="primary" onClick={this.acceptDialog}>Ok</Button>,
						<Button key='cancel' onClick={this.closeDialog}>Cancel</Button>
					]}>
					<DropdownView
						label='Service'
						required={true}
						model={(this.service) ? this.service.productServiceTypeId.toString() : null}
						disabled={this.props.mode == 'display'}
						onChanged={(item) => this.service = this.props.services.find(x => x.productServiceTypeId == parseInt(item.value))}
						options={this.servicesOpt}
					/>
				</Modal>
				<Modal
					visible={this.state.showPriceConfigDialog != null}
					onCancel={this.cancelPriceConfig}
					title="Price configuration"
					maskClosable={false}
					footer={[
						<Button key='confirm' type="primary" onClick={this.closePriceConfigDialog}>Ok</Button>,
						<Button key='cancel' onClick={this.cancelPriceConfig}>Cancel</Button>
					]}>
					{
						this.hardCodedModificationsConfigs.length > 0 && <DropdownView
							label='Predefined configurations for modifications'
							model={this.state.defaultPaxConfig}
							keyField="id"
							textField="name"
							options={this.hardCodedModificationsConfigs}
							onChanged={(defaultPaxConfig: DefaultPaxConfig) => {
								if (defaultPaxConfig) {
									let showPriceItem = this.state.showPriceItem;
									let showPriceConfigDialog = this.state.showPriceConfigDialog;
									let priceVMNotObservable = (showPriceConfigDialog as PriceViewModel).item;
									if (showPriceItem.displayName['en'] == null || showPriceItem.displayName['en'] == '') {
										showPriceItem.displayName['en'] = defaultPaxConfig.name;

									}
									priceVMNotObservable.checkMatchedPeopleInRestriccions = defaultPaxConfig.matchConfig.checkMatchedPeopleInRestriccions;
									priceVMNotObservable.minAge = defaultPaxConfig.matchConfig.minAge;
									priceVMNotObservable.maxAge = defaultPaxConfig.matchConfig.maxAge;
									priceVMNotObservable.minPax = defaultPaxConfig.matchConfig.minPax;
									priceVMNotObservable.maxPax = defaultPaxConfig.matchConfig.maxPax;
									priceVMNotObservable.minDuration = defaultPaxConfig.matchConfig.minDuration;
									priceVMNotObservable.maxDuration = defaultPaxConfig.matchConfig.maxDuration;
									showPriceItem.otherPaxsConfig = [];
									for (const otherConfig of defaultPaxConfig.otherPaxConfig) {
										showPriceItem.otherPaxsConfig.push({
											minAge: otherConfig.minAge,
											maxAge: otherConfig.maxAge,
											minPax: otherConfig.minPax,
											maxPax: otherConfig.maxPax,
											minDuration: otherConfig.minDuration,
											maxDuration: otherConfig.maxDuration,
										});
									}
									this.setState({
										defaultPaxConfig: defaultPaxConfig.id,
										showPriceItem: showPriceItem,
										showPriceConfigDialog: showPriceConfigDialog
									});
									this.forceUpdate();
								}

							}}
						/>
					}
					{
						this.state.showPriceConfigDialog &&
						<label>{
							this.state.showPriceItem.checkMatchedPeopleInRestriccions
								? 'Applied to all paxs that fulfill this conditions'
								: 'Applied to other paxs that fulfill this conditions'
						}</label>
					}
					{
						this.state.showPriceConfigDialog && <table className="b-table">
							<thead>
								<tr>
									<th rowSpan={2}>
										<TooltipView title='If check, matched pax that fulfill this primary condition also must satisfy with additional conditions'>
											<ZuluIcon type='info' />
										</TooltipView>
									</th>
									<th colSpan={2}>Pax</th>
									<th colSpan={2}>Age</th>
									<th colSpan={2}>Duration</th>
								</tr>
								<tr>
									<th>Min</th>
									<th>Max</th>
									<th>Min</th>
									<th>Max</th>
									<th>Min</th>
									<th>Max</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>
										<BooleanView
											value={this.state.showPriceItem.checkMatchedPeopleInRestriccions}
											label={null}
											onChanged={newVal => this.state.showPriceItem.checkMatchedPeopleInRestriccions = newVal}
											mode={this.props.mode}
										/>
									</td>
									<td style={{ width: 75 }} className='cell'>
										{
											this.getEditor(this.state.showPriceConfigDialog, 'minPax')
										}
									</td>
									<td style={{ width: 75 }} className='cell'>
										{
											this.getEditor(this.state.showPriceConfigDialog, 'maxPax')
										}
									</td>
									<td style={{ width: 75 }} className='cell'>
										{
											this.getEditor(this.state.showPriceConfigDialog, 'minAge')
										}
									</td>
									<td style={{ width: 75 }} className='cell'>
										{
											this.getEditor(this.state.showPriceConfigDialog, 'maxAge')
										}
									</td>
									<td style={{ width: 75 }} className='cell'>
										{
											this.getEditor(this.state.showPriceConfigDialog, 'minDuration')
										}
									</td>
									<td style={{ width: 75 }} className='cell'>
										{
											this.getEditor(this.state.showPriceConfigDialog, 'maxDuration')
										}
									</td>
								</tr>
							</tbody>
						</table>
					}
					<br />
					{
						this.state.showPriceConfigDialog && <label>
							Provided that the other paxs fulfill the following conditions
						</label>
					}
					{
						this.state.showPriceConfigDialog && <table className="b-table">
							<thead>
								<tr>
									<th rowSpan={2}>
										<Button icon={'plus'} onClick={
											() => {

												this.state.showPriceItem.otherPaxsConfig.push({
													minPax: 1,
													maxPax: 1,
													minAge: 0,
													maxAge: 99,
													minDuration: 1,
													maxDuration: 255
												});

												this.forceUpdate();
											}
										} disabled={this.props.mode == 'display'} />
									</th>
									<th colSpan={2}>Pax</th>
									<th colSpan={2}>Age</th>
									<th colSpan={2}>Duration</th>
								</tr>
								<tr>
									<th>Min</th>
									<th>Max</th>
									<th>Min</th>
									<th>Max</th>
									<th>Min</th>
									<th>Max</th>
								</tr>
							</thead>
							<tbody>
								{
									this.getOtherPaxsConfig(this.state.showPriceItem)
								}
							</tbody>
						</table>
					}
				</Modal>

				<Button icon="plus" key="NewServicePrice" disabled={this.props.mode == 'display'
					|| this.props.dates == null || this.props.dates.length == 0
					|| this.props.services == null || this.props.services.length == 0
					|| this.props.currency == null}
					onClick={(e) => { e.preventDefault(); this.newPrice(); }}
					className='mb-3'>
					New service's price
				</Button>
				{
					data
				}
			</div>
		)
	}

	private cancelPriceConfig = () => {
		this.setState({
			showPriceItem: undefined,
			showPriceConfigDialog: null,
			showPriceConfigDialogObservable: null,
			defaultPaxConfig: null
		});
	}

	@autobind
	private closePriceConfigDialog() {
		let priceVMNotObservable = (this.state.showPriceConfigDialog as PriceViewModel);
		let priceVMObservable = (this.state.showPriceConfigDialogObservable as PriceViewModel);
		let item = (this.state.showPriceItem as PriceElement);
		item.minAge = priceVMNotObservable.item.minAge;
		item.minPax = priceVMNotObservable.item.minPax;
		item.minDuration = priceVMNotObservable.item.minDuration;
		item.maxAge = priceVMNotObservable.item.maxAge;
		item.maxPax = priceVMNotObservable.item.maxPax;
		item.maxDuration = priceVMNotObservable.item.maxDuration;
		let prices = priceVMObservable.prices.filter(x => x.uuid == item.uuid);
		for (const price of prices) {
			price.otherPaxsConfig = item.otherPaxsConfig;
			price.minAge = item.minAge;
			price.minPax = item.minPax;
			price.minDuration = item.minDuration;
			price.maxAge = item.maxAge;
			price.maxPax = item.maxPax;
			price.maxDuration = item.maxDuration;
			price.displayName = item.displayName
			priceVMObservable.array[0].dynamicProperties = price.displayName;
		}

		this.cancelPriceConfig();
	}

	@autobind
	groupByData(items: PriceElement[], startEditing?: boolean): any {
		const itemsByServices = this.groupPricesByServices(items);
		const results = [];
		for (const item of itemsByServices) {
			let rows: PriceViewModel[] = [];
			for (const i of item.prices) {
				let obj: any = rows.find(e => i.uuid == e.item.uuid);
				if (obj == null) {
					obj = new PriceViewModel(i, this, startEditing);
					rows.push(obj);
				} else {
					let p = obj.prices.find(x => x == i);
					if (p == null)
						obj.add(i, this);
				}
			}
			rows.sort((a, b) => {
				if (a.item.type == b.item.type) {
					if (a.item.type == 'Price') {
						return a.item.maxDuration < b.item.maxDuration
							? -1
							: a.item.maxDuration > b.item.maxDuration
								? 1
								: a.item.minPax < b.item.minPax
									? -1
									: a.item.maxPax < b.item.maxPax
										? -1
										: 1;
					} else {
						return a.item.order - b.item.order;
					}
				} else {
					return a.item.type == 'Price' ? -1 : 1;
				}
			});
			results.push({
				service: item.service,
				prices: rows
			});
		}
		return results;
	}

	getColumnKey(item: DateRange) {
		return `${new Date(item.bookingDateFrom).valueOf()}_${new Date(item.bookingDateTo).valueOf()}_${new Date(item.serviceDateFrom).valueOf()}_${new Date(item.serviceDateTo).valueOf()}`;
	}

	getDates(item: string): moment.Moment[] {
		return item.split('_').map(e => moment.utc(new Date(parseInt(e))));
	}

	private groupPricesByServices(prices: any[]) {
		if (this.props.services == null || this.props.services.length == 0) {
			return [];
		}

		prices.sort((x, y) => x.service - y.service);
		const tables = [];
		let latestService = null;
		for (const price of prices) {
			if (latestService == null || latestService != price.service) {
				let pi = this.props.services.find(e => price.service == e.productServiceTypeId);
				if (pi != null) {
					tables.push({
						service: this.props.services.find(e => price.service == e.productServiceTypeId),
						prices: [price]
					});
					latestService = price.service;
				}

			} else {
				tables[tables.length - 1].prices.push(price);
			}
		}
		return tables;
	}

	private getPriceModification(priceModification: PriceViewModel, property: keyof PriceElement | string, pricesGroup: PriceViewModel[]) {
		const value = priceModification[property];
		const sign = priceModification.item.sign;
		const mode = priceModification.item.mode;
		const prices = pricesGroup.map(pr => Object.getOwnPropertyNames(pr.item).every(p => p != property) ? pr[property] : pr.item[property]);

		const realPrice = [];
		for (let i = 0; i < prices.length; i++) {
			const price = parseFloat(prices[i]);
			let newVal: number = 0;
			if (mode == 'Amount') {
				newVal = value;
			} else {
				newVal = value * price;
			}

			if (sign == 'Up') {
				newVal = price + newVal;
			} else {
				newVal = price - newVal;
			}

			realPrice.push(
				<li key={i}>
					{`${price} --> ${newVal}`}
				</li>
			);
		}

		return (
			<ul className="tooltip-list">
				{realPrice}
			</ul>
		);
	}

	private getConditions(price: PriceElement | PaxConfig, showTitle: boolean = true) {
		const conditions: React.ReactNode[] = [];
		conditions.push(<li key="minPax">{`Min Pax --> ${price.minPax}`}</li>);
		conditions.push(<li key="maxPax">{`Max Pax --> ${price.maxPax}`}</li>);
		conditions.push(<li key="minAge">{`Min Age --> ${price.minAge}`}</li>);
		conditions.push(<li key="maxAge">{`Max Age --> ${price.maxAge}`}</li>);
		conditions.push(<li key="minDuration">{`Min Duration --> ${price.minDuration}`}</li>);
		conditions.push(<li key="maxDuration">{`Max Duration --> ${price.maxDuration}`}</li>);
		const priceElement = (price as PriceElement);
		let otherPaxsConfig = priceElement.otherPaxsConfig;
		if (otherPaxsConfig) {
			conditions.push(
				<li key="additionalConfig">
					<br />
					Additional conditions
					<ul className="tooltip-list">
						{otherPaxsConfig.map((e, i) => (
							<li key={`${e.minPax}${e.maxPax}${e.minDuration}${e.maxDuration}${e.minAge}${e.maxAge}`}>
								{i + 1} --> {this.getConditions(e, false)}
							</li>
						))}
					</ul>
				</li>
			);
		}
		return (
			<React.Fragment>
				{showTitle && 'Configure conditions for price modification'}
				<ul className="tooltip-list">
					{conditions}
				</ul>
			</React.Fragment>
		);
	}

	private getEditor(price: PriceViewModel, property: keyof PriceElement | string, applyFormat?: string) {
		const value = Object.getOwnPropertyNames(price.item).every(p => p != property) ? price[property] : price.item[property];
		return <TextView mode='edit' label={null} value={value}
			suffix={applyFormat ? applyFormat : undefined}
			format={applyFormat == '%' ? 'percent' : undefined}
			onChanged={newVal => {
				newVal = newVal != null ? parseFloat(newVal) : null;
				this.updatePriceElement(price, property, newVal);
			}} />;
	}

	private getOtherPaxsConfig(priceModification: PriceElement) {
		let otherPaxsConfig = (priceModification.otherPaxsConfig || []);
		return otherPaxsConfig.map(paxConfig => <tr key={uuid.v4()}>
			<td style={{ width: 40 }} className='cell'>
				<Button icon={'delete'} onClick={() => {
					const minPax = paxConfig.minPax;
					const maxPax = paxConfig.maxPax;
					const minAge = paxConfig.minAge;
					const maxAge = paxConfig.maxAge;
					const minDuration = paxConfig.minDuration;
					const maxDuration = paxConfig.maxDuration;
					priceModification.otherPaxsConfig.splice(priceModification.otherPaxsConfig
						.findIndex(e => e.minPax == minPax && e.maxPax == maxPax
							&& e.minAge == minAge && e.maxAge == maxAge
							&& e.minDuration == minDuration && e.maxDuration == maxDuration), 1);

					this.forceUpdate();
				}} disabled={this.props.mode == 'display'} />
			</td>
			<td style={{ width: 75 }} className='cell'>
				<TextView mode='edit' label={null} value={paxConfig.minPax}
					onChanged={newVal => {
						const config = this.getConfigs(priceModification, paxConfig);
						if (config)
							config.minPax = parseInt(newVal);

						paxConfig.minPax = parseInt(newVal);
					}} />
			</td>
			<td style={{ width: 75 }} className='cell'>
				<TextView mode='edit' label={null} value={paxConfig.maxPax}
					onChanged={newVal => {
						const config = this.getConfigs(priceModification, paxConfig);
						if (config)
							config.maxPax = parseInt(newVal);
						paxConfig.maxPax = parseInt(newVal);
					}} />
			</td>
			<td style={{ width: 75 }} className='cell'>
				<TextView mode='edit' label={null} value={paxConfig.minAge}
					onChanged={newVal => {
						const config = this.getConfigs(priceModification, paxConfig);
						if (config)
							config.minAge = parseInt(newVal);
						paxConfig.minAge = parseInt(newVal);
					}} />
			</td>
			<td style={{ width: 75 }} className='cell'>
				<TextView mode='edit' label={null} value={paxConfig.maxAge}
					onChanged={newVal => {
						const config = this.getConfigs(priceModification, paxConfig);
						if (config)
							config.maxAge = parseInt(newVal);
						paxConfig.maxAge = parseInt(newVal);
					}} />
			</td>
			<td style={{ width: 75 }} className='cell'>
				<TextView mode='edit' label={null} value={paxConfig.minDuration}
					onChanged={newVal => {
						const config = this.getConfigs(priceModification, paxConfig);
						if (config)
							config.minDuration = parseInt(newVal);
						paxConfig.minDuration = parseInt(newVal);
					}} />
			</td>
			<td style={{ width: 75 }} className='cell'>
				<TextView mode='edit' label={null} value={paxConfig.maxDuration}
					onChanged={newVal => {
						const config = this.getConfigs(priceModification, paxConfig);
						if (config)
							config.maxDuration = parseInt(newVal);
						paxConfig.maxDuration = parseInt(newVal);
					}} />
			</td>
		</tr>);
	}

	private getConfigs(priceModification: PriceElement, paxConfig: PaxConfig) {
		return priceModification.otherPaxsConfig.find(e => e.minPax == paxConfig.minPax && e.maxPax == paxConfig.maxPax
			&& e.minAge == paxConfig.minAge && e.maxAge == paxConfig.maxAge
			&& e.minDuration == paxConfig.minDuration && e.maxDuration == paxConfig.maxDuration);
	}
}

const mapStateToProps = (
	state: IApplicationState,
	props: Props
): PropsFromState => {
	const contract = state.otherLoadedEntities.find(e => e.entity == props.contract.entity) as ContractItem;
	return {
		dates: contract.dateBands,
		services: contract.services,
		markets: contract.markets,
		product: contract.product,
	};
};

const connected = connect(mapStateToProps)(ContractPriceView);
export { connected as ContractPriceView };



// WEBPACK FOOTER //
// ./src/app/products/contract/form/parts/contract-price.view.tsx